import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CookieService } from './cookie.service';
import { AutenticacaoDto, TipoArmazenamento, TipoAutenticacao } from './modelos/autenticacao-dto';
import { HasPermissionsInput } from './modelos/has-permissions-input';
import { HasPermissionsResultDto } from './modelos/has-permissions-result-dto';
import { TokenDto } from './modelos/token-dto';



export enum AutenticacaoStatus {
  NaoAutenticado,
  Validacao,
  Certificado
}

export class AutenticacaoInfo {
  token: string = '';
  nome: string = '-';
  numeroInscricao: string = '-';
  numeroInscricaoTexto: string = '-';
  status: AutenticacaoStatus = AutenticacaoStatus.NaoAutenticado;
  certificadoTipo: string = '-';
  certificadoId: string = '-';

  constructor(init?: Partial<AutenticacaoInfo>) {
    Object.assign(this, init);
  }
}

@Injectable({
  providedIn: 'root'
})
export class AutenticacaoService {
  private PORTARIA_TOKEN_API = 
    environment.production ? 'https://api.portaria.smosc.com.br/autenticacao/token/{cd}' : 'https://api.portaria.localhost.smosc.com.br/autenticacao/token/{cd}';
  private PORTARIA_TOKEN_ATUALIZACAO_API = 
    environment.production ? 'https://api.portaria.smosc.com.br/autenticacao/token/atualizacao' :  'https://api.portaria.localhost.smosc.com.br/autenticacao/token/atualizacao';
  private PORTARIA_URL = 
    environment.production ? 'https://portaria.smosc.com.br/' : 'https://portaria.localhost.smosc.com.br/';
  private HAS_PERMISSIONS_URL: string =
    environment.production ? 'https://api.portaria.smosc.com.br/permissoes/validacoes' :  'https://api.portaria.localhost.smosc.com.br/permissoes/validacoes';
  private PORTARIA_SESSAO_TERMINO_API = 
    environment.production ? 'https://api.portaria.smosc.com.br/sessao/termino' :  'https://api.portaria.localhost.smosc.com.br/sessao/termino';

  private SERVICO = 'smosaude';
  
  constructor(private http: HttpClient, private cookie: CookieService) { }

  inicializacaoStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  autenticacaoStatus: BehaviorSubject<AutenticacaoDto> = new BehaviorSubject<AutenticacaoDto>(new AutenticacaoDto());

  inicializacao(cd: string | null = null, salvar: TipoArmazenamento = TipoArmazenamento.Sessao): Observable<boolean> {
    if(cd === 'anonymous') {
      return this.inicializarAnonimo().pipe(map(_ => true));
    } else if(cd) {
      return this.inicializarAutenticacaoRemota(cd, salvar).pipe(map(_ => true));
    } else {
      return this.inicializarAutenticacaoDoArmazenamento().pipe(map(_ => true));
    }
  }

  inicializarAnonimo(): Observable<AutenticacaoDto> {
    let autenticacao = new AutenticacaoDto();

    this.limparAutenticacao();
    this.inserirAutenticacaoNoArmazenamento(autenticacao)

    this.autenticacaoStatus.next(autenticacao);
    this.inicializacaoStatus.next(true);

    return of(autenticacao);
  }

  // Nesse forma de autenticação é considerado que as informações de autenticação já estão no armazenamento
  // local/cookie sessão.
  inicializarAutenticacaoDoArmazenamento(): Observable<AutenticacaoDto> {
    let url = this.PORTARIA_TOKEN_ATUALIZACAO_API;

    let autenticacao: AutenticacaoDto | null = this.consultarAutenticacao();

    if((!autenticacao || this.autenticacaoVencida(autenticacao)) && this.rotaAtualAbertaParaAnonimos()) 
      return this.inicializarAnonimo();

    if(!autenticacao || this.autenticacaoVencida(autenticacao)) {
      this.executarRedirecionamento();
      return of(new AutenticacaoDto());
    }

    // Futuramente a autenticação de anonimos também vai ser remota,
    // atualmente não valida remotamente.
    if(autenticacao.tipoAutenticacao === TipoAutenticacao.Anonimo){
      this.autenticacaoStatus.next(autenticacao);
      this.inicializacaoStatus.next(true);
      return of(new AutenticacaoDto());
    }

    return this.http.put<TokenDto>(url, autenticacao.token)
    .pipe(
      map((token: TokenDto) => {  
        if(autenticacao == null)
          throw `autenticacao is null.`;

        autenticacao.token = token;
        
        this.inserirAutenticacaoNoArmazenamento(autenticacao);
          
        this.autenticacaoStatus.next(autenticacao);
        this.inicializacaoStatus.next(true);

        return  autenticacao;
      }),
      catchError((err: HttpErrorResponse) => {
        this.limparAutenticacao();
        this.inicializacaoStatus.next(false);

        if(err.status === 404 || err.status === 401) {
          this.executarRedirecionamento();
        } else {
          this.executarRedirecionamento(err?.status ?? -200);
        }

        throw err;
      })
    );
  }

  private rotaAtualAbertaParaAnonimos(): boolean {
    return window.location.pathname?.startsWith('/validacao') ?? false ? true : false;
  }

  private autenticacaoVencida(autenticacao: AutenticacaoDto) {
    return new Date() > autenticacao.token.expiraEm ? true : false;
  }

  private inserirAutenticacaoNoArmazenamento(autenticacao: AutenticacaoDto) {
    if(autenticacao.tipoArmazenamento === TipoArmazenamento.Local) {
      this.limparAutenticacao();
      localStorage.setItem('autenticacao', JSON.stringify(autenticacao));
    } else {
      this.limparAutenticacao();
      this.cookie.setarNaSessao('autenticacao',  JSON.stringify(autenticacao));
    }
  }

  private inicializarAutenticacaoRemota(cd: string, salvar: TipoArmazenamento): Observable<AutenticacaoDto> {
    let url = this.PORTARIA_TOKEN_API.replace('{cd}', cd);
    let autenticacao = new AutenticacaoDto();
    autenticacao.tipoArmazenamento = salvar;
    autenticacao.tipoAutenticacao = TipoAutenticacao.Validado;

    return this.http.get<TokenDto>(url)
    .pipe(
      tap((token: TokenDto) => {
        autenticacao.token = token;

        this.inserirAutenticacaoNoArmazenamento(autenticacao)

        this.autenticacaoStatus.next(autenticacao);
        this.inicializacaoStatus.next(true);
      }),
      map(_ => autenticacao),
      catchError((err: HttpErrorResponse) => {        
        this.limparAutenticacao();
        this.inicializacaoStatus.next(false);

        if(err.status === 404) {
          this.executarRedirecionamento();
        } else {
          this.executarRedirecionamento(err?.status ?? -100);
        }

        throw err;
      })
    );
  }

  terminarSessao(): Observable<any> {
    let url = this.PORTARIA_SESSAO_TERMINO_API;

    let autenticacao = this.consultarAutenticacao();
    
    if(!autenticacao || autenticacao.tipoAutenticacao !== TipoAutenticacao.Validado) {
      this.limparAutenticacao();
      this.executarRedirecionamento(null, true);
      return of(true);
    }

    return this.http.post<TokenDto>(url, autenticacao.token)
    .pipe(
      tap(() => {   
        this.limparAutenticacao();
        this.executarRedirecionamento(null, true);
      }),
      catchError((err: HttpErrorResponse) => {
        this.limparAutenticacao();
        this.executarRedirecionamento(null, true);
        throw err;
      })
    );
  }

  public executarRedirecionamento(err: number | null = null, apenasOrigem: boolean = false) {
    window.location.href = this.montarUrlRedirecionamento(this.SERVICO, this.gerarUrlRetorno(apenasOrigem), err);
  }

  private limparAutenticacao() {
    localStorage.removeItem('autenticacao');
    this.cookie.remover('autenticacao');
  }

  public consultarAutenticacao(): AutenticacaoDto | null {
    let autenticacao = localStorage.getItem('autenticacao') ?? '';

    if(!autenticacao) {
      autenticacao = this.cookie.consultar('autenticacao');
    }

    return autenticacao ? JSON.parse(autenticacao) : null;
  }

  private tipoArmazenamento(): TipoArmazenamento | null {
    let autenticacaoSessao = this.cookie.consultar('autenticacao');
    let autenticacaoLocal = localStorage.getItem('autenticacao');

    if(autenticacaoSessao) {
      return TipoArmazenamento.Sessao;
    } 

    if(autenticacaoLocal) {
      return TipoArmazenamento.Local;
    } 

    return null;
  }
  
  private gerarUrlRetorno(apenasOrigem: boolean = false): string {
    if(apenasOrigem) {
      return window.location.origin;
    }

    let uri = window.location.toString().replace(/cd=[a-z0-9]+/i, '');
    uri = uri.replace(/\?$/, '');
    uri = uri.replace(/&&/g, '&');
    uri = uri.replace(/\?&/g, '?');

    uri = uri.replace(/s=[a-z0-9]+/i, '');
    uri = uri.replace(/\?$/, '');
    uri = uri.replace(/&&/g, '&');
    uri = uri.replace(/\?&/g, '?');

    let uriRetorno = encodeURI(uri);

    return uriRetorno;
  }

  private montarUrlRedirecionamento(servico: string, retorno: string, err: number | null = null) {
    let url = `${this.PORTARIA_URL}?servico=${servico}&recurso=${retorno}`;

    if(err !== null) 
      url = url + `&err=${err + ''}`;

    return url;
  }

  // se any = true, retorna true se o usuário tem pelo menos uma das permissões.
  // se any = false, return true apenas se o usuário possui todas as permissões.
  public possuiPermissoes(permissions: string[], any: boolean): Observable<HasPermissionsResultDto> {
    //return this.http.post<Aso>(this.URL, levantamentoInput);

    return this.http.post<HasPermissionsResultDto>(this.HAS_PERMISSIONS_URL, new HasPermissionsInput({ 
      permissions: permissions, any: any
     }));
  }
}
